import React, { createContext, useContext, useState, useEffect } from "react";

const I18nContext = createContext(null);

export const translations = {
  es: {
    nav: {
      home: "Inicio",
      products: "Productos",
      flora: "Flora IA",
      about: "Nosotros",
      historia: "Historia",
      contact: "Contacto",
      quote: "Solicitar cotización",
    },
    hero: {
      eyebrow: "Agricultura de precisión",
      title_l1: "Nutrición vegetal",
      title_l2: "que rinde frutos",
      subtitle:
        "EUZERONE — Fertilizantes y bioestimulantes premium formulados para el productor profesional. Calidad, ciencia y campo.",
      cta_primary: "Explorar productos",
      cta_secondary: "Hablar con Flora",
      stat1_label: "Productos especializados",
      stat1_value: "05",
      stat2_label: "Cultivos compatibles",
      stat2_value: "+20",
      stat3_label: "Uso profesional",
      stat3_value: "100%",
    },
    about: {
      eyebrow: "Sobre Euzerone",
      title: "Ciencia agrícola al servicio del rendimiento",
      p1: "Euzerone formula nutrientes, bioestimulantes y correctores diseñados para resolver los retos más comunes del campo profesional: clorosis, cuaje deficiente, engorde irregular y resistencia a estrés.",
      p2: "Cada producto sigue normativas europeas y se garantiza con análisis verificables — Clase A en metales pesados y compatible con agricultura ecológica cuando corresponde.",
      bullet1: "Fórmulas con micronutrientes quelatados (EDTA, EDDHA)",
      bullet2: "Aptos para aplicación foliar y fertirriego",
      bullet3: "Compatibles con la mayoría de fertilizantes",
      bullet4: "Conformidad CE y trazabilidad garantizada",
      seal_eu: "Normativa UE",
      seal_metals: "Clase A",
      seal_metals_desc: "Garantía en metales pesados",
      seal_organic: "Ecológico",
    },
    products: {
      eyebrow: "Catálogo",
      title: "Productos pensados para cada etapa del cultivo",
      subtitle:
        "Desde el enraizamiento hasta la postcosecha, soluciones específicas con respaldo agronómico.",
      view_detail: "Ver detalle",
      request_quote: "Solicitar cotización",
    },
    diagnostico: {
      eyebrow: "Diagnóstico en campo",
      title: "Dinos qué ves. Te decimos qué aplicar.",
      subtitle:
        "Selecciona el síntoma que observas en tu cultivo y te indicamos la corrección recomendada, con su dosis.",
      idle: "Elige un síntoma para ver la corrección recomendada.",
      ask_flora: "Consultar con Flora",
      disclaimer:
        "Las recomendaciones son orientativas y no sustituyen el criterio de un técnico agrónomo. Consulta siempre la ficha técnica del producto antes de su aplicación.",
      symptoms: {
        clorosis: "Hojas amarillas entre los nervios",
        cuaje: "La flor no cuaja",
        calibre: "Fruto sin calibre ni color",
        estres: "Planta parada tras estrés",
        nematodos: "Raíz dañada, presencia de nemátodos",
        suelo: "Suelo agotado, poca materia orgánica",
      },
    },
    perfiles: {
      eyebrow: "Tres formas de trabajar con nosotros",
      title: "No compra igual quien cultiva que quien distribuye.",
      subtitle: "Entra por tu perfil y verás solo lo que te sirve.",
      items: {
        agricultor: {
          title: "Agricultor",
          desc: "Tienes un problema concreto en campo y necesitas saber qué aplicar, cuánto y cuándo.",
          bullets: [
            "Ficha técnica y dosis por cultivo",
            "Aplicación foliar y fertirriego",
            "Compatibilidad con tu programa actual",
          ],
          cta: "Ver catálogo",
        },
        cooperativa: {
          title: "Cooperativa y gran cultivo",
          desc: "Compras volumen y necesitas cuadrar coste por hectárea con garantía de suministro.",
          bullets: [
            "Cotización por volumen",
            "Documentación y conformidad UE",
            "Asesoramiento agronómico",
          ],
          cta: "Solicitar cotización",
        },
        distribuidor: {
          title: "Distribuidor",
          desc: "Quieres incorporar la gama a tu catálogo y necesitas condiciones y soporte técnico.",
          bullets: [
            "Condiciones comerciales",
            "Cobertura por zona",
            "Material técnico y formación",
          ],
          cta: "Quiero distribuir",
        },
      },
    },
    contacto: {
      eyebrow: "Hablemos",
      title: "Estamos al otro lado.",
      subtitle:
        "Consultas técnicas, cotizaciones o condiciones de distribución. Elige el canal que prefieras.",
      sede: "Barcelona, España · Atención en español e inglés",
      canales: {
        telefono: {
          label: "Teléfono",
          nota: "De lunes a viernes, horario comercial.",
        },
        whatsapp: {
          label: "WhatsApp",
          nota: "Respuesta rápida, también fuera de horario con Flora.",
        },
        email: {
          label: "Correo",
          nota: "Para documentación técnica y consultas detalladas.",
        },
      },
    },
    testimonials: {
      eyebrow: "Casos de uso",
      title: "Resultados verificados en campo",
      subtitle: "Productores europeos que confían en EUZERONE.",
      items: [
        {
          name: "Lucía Martín",
          role: "Productora de cítricos · Valencia",
          quote: "Aplicamos Hierro 6-48 al inicio de brotación y la clorosis desapareció en tres semanas. La hoja recuperó el verde intenso y el cuaje fue superior al año anterior.",
          product: "Hierro 6-48",
          crop: "Cítricos",
        },
        {
          name: "Bodegas Aramendi",
          role: "Viticultor · DOC Rioja",
          quote: "Con Nuster Flower y Engorde Complet logramos racimos más homogéneos y mayor °Brix en vendimia. La maduración fue uniforme sin perder vida postcosecha.",
          product: "Nuster Flower + Engorde Complet",
          crop: "Vid",
        },
        {
          name: "Hortícolas del Sur",
          role: "Productor de tomate y pimiento · Almería",
          quote: "Incorporamos Nautic en el programa de fertirriego y el cultivo respondió mejor al estrés térmico de verano. Notamos enraizamiento más vigoroso y menos abortos de flor.",
          product: "Nautic",
          crop: "Hortícolas",
        },
      ],
    },
    historia: {
      eyebrow: "Nuestra historia",
      title: "Donde nace la raíz, nace la vida",
      intro1: "Euzerone nació de una intuición sencilla pero poderosa: si cuidamos la raíz, todo lo demás florece.",
      intro2: "Antes de ser una marca, Euzerone fue una pregunta: ¿Cómo podemos devolverle al suelo lo que el suelo nos da? ¿Cómo crear productos que no solo alimenten, sino que despierten la fuerza natural de cada planta?",
      intro3: "La respuesta llegó desde la observación del campo, del olor a tierra húmeda, de la paciencia del agricultor que sabe que cada brote es un pequeño milagro. Euzerone surgió ahí, en ese punto donde la ciencia se encuentra con la vida y la transforma.",
      intro4: "La marca comenzó desarrollando formulaciones técnicas en Europa, donde la agricultura de precisión y la normativa ecológica marcan estándares muy altos. Desde allí, dio un salto natural hacia Hispanoamérica y el Caribe.",
      tagline: "Euzerone. Donde empieza la vida. Donde empieza todo.",
      lema: "Fertilidad con propósito, precisión con visión.",
      pillarsLabel: "Los 3 Pilares",
      pillars: [
        {
          icon: "fa-solid fa-earth-americas",
          title: "Un puente entre mundos",
          text: "Nacimos entre dos tierras: la precisión técnica de Europa y la vitalidad de los cultivos tropicales. De ese contraste nació nuestra esencia: productos capaces de rendir en cualquier clima, en cualquier suelo, en cualquier desafío.",
        },
        {
          icon: "fa-solid fa-flask",
          title: "Ciencia con alma",
          text: "Desde el primer día, Euzerone se construyó sobre un principio: cada producto debe demostrar su eficacia en campo antes de llegar al mercado. Cada fórmula es un acto de respeto hacia la naturaleza.",
        },
        {
          icon: "fa-solid fa-seedling",
          title: "Crecemos contigo",
          text: "Euzerone no es solo una marca de enraizantes y fertilizantes. Es una forma de entender la agricultura: cuidar la raíz, fortalecer el suelo, acompañar al agricultor y honrar el ciclo natural.",
        },
      ],
      closing: "Por eso decimos que Euzerone no se aplica: se siente.",
      visionLabel: "Visión",
      vision: "Queremos que cada agricultor, desde el Mediterráneo hasta el Caribe, sienta que no está solo. Que tiene a su lado una marca que cree en la vida, en la tierra y en el poder de una raíz bien cuidada.",
      misionLabel: "Misión",
      mision: "Ofrecer soluciones agronómicas de precisión europea, adaptadas a los cultivos más exigentes de España e Hispanoamérica, con productos verificados en campo, sostenibles y accesibles para el agricultor profesional.",
      expansionLabel: "Expansión",
      expansion: "Hoy Euzerone está presente en España, República Dominicana y varios países de Hispanoamérica, con crecimiento sostenido hacia Italia, Portugal, Francia, Colombia, México y el mercado hispano de EE.UU.",
    },
    productDetail: {
      composition: "Composición garantizada",
      benefits: "Beneficios principales",
      dosage: "Dosis recomendada",
      foliar: "Foliar",
      fertigation: "Fertirriego",
      crops: "Cultivos / Uso",
      category: "Categoría",
      formula: "Fórmula",
      back: "Cerrar",
      request_quote: "Solicitar cotización",
      ask_flora: "Preguntar a Flora",
    },
    flora: {
      eyebrow: "Asesoría IA",
      title: "Conoce a Flora",
      subtitle: "Tu asesora agrícola disponible 24/7",
      description:
        "Flora es nuestra agente de inteligencia artificial entrenada en el catálogo Euzerone y en agronomía aplicada. Te ayuda a elegir el producto correcto según cultivo, fase fenológica y problema observado en campo.",
      bullet1: "Recomendaciones según cultivo y problema",
      bullet2: "Composición, dosis y momento de aplicación",
      bullet3: "Disponible en español e inglés, 24/7",
      cta: "Chatear con Flora",
    },
    chat: {
      title: "Flora — Asesora IA",
      subtitle: "En línea • Powered by Euzerone",
      placeholder: "Pregunta a Flora sobre tu cultivo…",
      send: "Enviar",
      greeting:
        "¡Hola! Soy Flora, tu asesora agrícola Euzerone. Cuéntame qué cultivo manejas o qué problema observas en campo y te recomendaré el producto adecuado.",
      sample_q1: "¿Cómo corrijo clorosis férrica en cítricos?",
      sample_q2: "Tengo problemas de cuaje en tomate",
      sample_q3: "Quiero mejorar el engorde de uva",
      thinking: "Flora está pensando…",
      error: "Hubo un problema. Inténtalo de nuevo.",
      open: "Hablar con Flora",
    },
    quote: {
      title: "Solicitar cotización",
      subtitle: "Cuéntanos sobre tu cultivo y te contactaremos en menos de 24h.",
      name: "Nombre completo",
      email: "Correo electrónico",
      phone: "Teléfono (opcional)",
      company: "Empresa / Finca",
      country: "País",
      product: "Producto de interés",
      quantity: "Cantidad estimada",
      message: "Mensaje o detalle del cultivo",
      submit: "Enviar solicitud",
      success: "¡Solicitud enviada! Te contactaremos pronto.",
      error: "Hubo un error. Inténtalo de nuevo.",
      any_product: "— Selecciona un producto —",
      submitting: "Enviando…",
    },
    footer: {
      tagline: "Nutrición vegetal premium para agricultura profesional.",
      products: "Productos",
      company: "Empresa",
      contact: "Contacto",
      legal: "Aviso legal",
      rights: "Todos los derechos reservados.",
    },
  },
  en: {
    nav: {
      home: "Home",
      products: "Products",
      flora: "Flora AI",
      about: "About",
      historia: "Story",
      contact: "Contact",
      quote: "Request quote",
    },
    hero: {
      eyebrow: "Precision agriculture",
      title_l1: "Plant nutrition",
      title_l2: "that bears fruit",
      subtitle:
        "EUZERONE — Premium fertilizers and biostimulants formulated for the professional grower. Quality, science and field.",
      cta_primary: "Explore products",
      cta_secondary: "Talk to Flora",
      stat1_label: "Specialized products",
      stat1_value: "05",
      stat2_label: "Compatible crops",
      stat2_value: "+20",
      stat3_label: "Professional use",
      stat3_value: "100%",
    },
    about: {
      eyebrow: "About Euzerone",
      title: "Agricultural science at the service of yield",
      p1: "Euzerone formulates nutrients, biostimulants and correctors designed to solve the most common challenges in professional farming: chlorosis, poor fruit set, irregular fattening and stress resistance.",
      p2: "Every product follows European regulations and is backed by verifiable analysis — Class A in heavy metals and suitable for organic farming where applicable.",
      bullet1: "Formulas with chelated micronutrients (EDTA, EDDHA)",
      bullet2: "Suitable for foliar application and fertigation",
      bullet3: "Compatible with most fertilizers",
      bullet4: "EC conformity and guaranteed traceability",
      seal_eu: "EU regulation",
      seal_metals: "Class A",
      seal_metals_desc: "Heavy metals guarantee",
      seal_organic: "Organic",
    },
    products: {
      eyebrow: "Catalog",
      title: "Products designed for every stage of the crop",
      subtitle:
        "From rooting to post-harvest, specific solutions with agronomic support.",
      view_detail: "View detail",
      request_quote: "Request quote",
    },
    diagnostico: {
      eyebrow: "Field diagnosis",
      title: "Tell us what you see. We'll tell you what to apply.",
      subtitle:
        "Select the symptom you observe in your crop and we'll show you the recommended correction, with its dosage.",
      idle: "Choose a symptom to see the recommended correction.",
      ask_flora: "Ask Flora",
      disclaimer:
        "These recommendations are indicative and do not replace the judgement of an agronomist. Always check the product data sheet before application.",
      symptoms: {
        clorosis: "Yellow leaves between the veins",
        cuaje: "Flowers failing to set",
        calibre: "Fruit lacking size and colour",
        estres: "Plant stalled after stress",
        nematodos: "Damaged roots, nematode presence",
        suelo: "Depleted soil, low organic matter",
      },
    },
    perfiles: {
      eyebrow: "Three ways to work with us",
      title: "Growers and distributors don't buy the same way.",
      subtitle: "Start from your profile and see only what's relevant to you.",
      items: {
        agricultor: {
          title: "Grower",
          desc: "You have a specific field problem and need to know what to apply, how much and when.",
          bullets: [
            "Data sheet and dosage by crop",
            "Foliar application and fertigation",
            "Compatibility with your current programme",
          ],
          cta: "View catalog",
        },
        cooperativa: {
          title: "Co-operative and large-scale",
          desc: "You buy in volume and need to balance cost per hectare with guaranteed supply.",
          bullets: [
            "Volume quotation",
            "Documentation and EU conformity",
            "Agronomic advice",
          ],
          cta: "Request a quote",
        },
        distribuidor: {
          title: "Distributor",
          desc: "You want to add the range to your catalog and need terms and technical support.",
          bullets: [
            "Commercial terms",
            "Territory coverage",
            "Technical material and training",
          ],
          cta: "Become a distributor",
        },
      },
    },
    contacto: {
      eyebrow: "Get in touch",
      title: "We're on the other side.",
      subtitle:
        "Technical questions, quotations or distribution terms. Choose whichever channel suits you.",
      sede: "Barcelona, Spain · Support in Spanish and English",
      canales: {
        telefono: {
          label: "Phone",
          nota: "Monday to Friday, business hours.",
        },
        whatsapp: {
          label: "WhatsApp",
          nota: "Fast replies, and out of hours with Flora.",
        },
        email: {
          label: "Email",
          nota: "For technical documentation and detailed enquiries.",
        },
      },
    },
    testimonials: {
      eyebrow: "Case studies",
      title: "Verified results in the field",
      subtitle: "European growers who trust EUZERONE.",
      items: [
        {
          name: "Lucía Martín",
          role: "Citrus grower · Valencia",
          quote: "We applied Hierro 6-48 at the start of bud break and chlorosis disappeared in three weeks. Leaves recovered deep green and fruit set was higher than the previous year.",
          product: "Hierro 6-48",
          crop: "Citrus",
        },
        {
          name: "Bodegas Aramendi",
          role: "Winemaker · DOC Rioja",
          quote: "With Nuster Flower and Engorde Complet we achieved more uniform clusters and higher °Brix at harvest. Ripening was even without sacrificing post-harvest life.",
          product: "Nuster Flower + Engorde Complet",
          crop: "Vineyard",
        },
        {
          name: "Hortícolas del Sur",
          role: "Tomato and pepper grower · Almería",
          quote: "We added Nautic to the fertigation program and the crop handled summer heat stress much better. We noticed stronger rooting and fewer flower abortions.",
          product: "Nautic",
          crop: "Vegetables",
        },
      ],
    },
    historia: {
      eyebrow: "Our story",
      title: "Where the root is born, life is born",
      intro1: "Euzerone was born from a simple yet powerful intuition: if we take care of the root, everything else flourishes.",
      intro2: "Before becoming a brand, Euzerone was a question: How can we give back to the soil what the soil gives us? How can we create products that not only feed, but awaken the natural strength of every plant?",
      intro3: "The answer came from field observation, the smell of damp earth, the patience of the farmer who knows every sprout is a small miracle. Euzerone emerged right there, at the point where science meets life and transforms it.",
      intro4: "The brand began developing technical formulations in Europe, where precision agriculture and ecological regulations set very high standards. From there, it took a natural leap toward Latin America and the Caribbean.",
      tagline: "Euzerone. Where life begins. Where everything begins.",
      lema: "Fertility with purpose, precision with vision.",
      pillarsLabel: "The 3 Pillars",
      pillars: [
        {
          icon: "fa-solid fa-earth-americas",
          title: "A bridge between worlds",
          text: "We were born between two lands: the technical precision of Europe and the vitality of tropical crops. From that contrast our essence emerged: products capable of performing in any climate, any soil, any challenge.",
        },
        {
          icon: "fa-solid fa-flask",
          title: "Science with soul",
          text: "From day one, Euzerone was built on one principle: every product must prove its efficacy in the field before reaching the market. Every formula is an act of respect toward nature.",
        },
        {
          icon: "fa-solid fa-seedling",
          title: "We grow with you",
          text: "Euzerone is not just a brand of rooting agents and fertilizers. It is a way of understanding agriculture: caring for the root, strengthening the soil, supporting the farmer and honoring the natural cycle.",
        },
      ],
      closing: "That's why we say Euzerone is not applied: it is felt.",
      visionLabel: "Vision",
      vision: "We want every farmer, from the Mediterranean to the Caribbean, to feel they are not alone. That they have by their side a brand that believes in life, in the earth, and in the power of a well-cared root.",
      misionLabel: "Mission",
      mision: "To offer agronomic solutions with European precision, adapted to the most demanding crops in Spain and Latin America, with field-verified, sustainable, and accessible products for the professional grower.",
      expansionLabel: "Expansion",
      expansion: "Today Euzerone is present in Spain, the Dominican Republic and several Latin American countries, with sustained growth toward Italy, Portugal, France, Colombia, Mexico and the Hispanic market in the U.S.",
    },
    productDetail: {
      composition: "Guaranteed composition",
      benefits: "Main benefits",
      dosage: "Recommended dosage",
      foliar: "Foliar",
      fertigation: "Fertigation",
      crops: "Crops / Use",
      category: "Category",
      formula: "Formula",
      back: "Close",
      request_quote: "Request quote",
      ask_flora: "Ask Flora",
    },
    flora: {
      eyebrow: "AI advisor",
      title: "Meet Flora",
      subtitle: "Your agricultural advisor 24/7",
      description:
        "Flora is our AI agent trained on the Euzerone catalog and applied agronomy. She helps you pick the right product based on crop, phenological stage and field issue.",
      bullet1: "Recommendations by crop and issue",
      bullet2: "Composition, dosage and timing",
      bullet3: "Available in Spanish and English, 24/7",
      cta: "Chat with Flora",
    },
    chat: {
      title: "Flora — AI advisor",
      subtitle: "Online • Powered by Euzerone",
      placeholder: "Ask Flora about your crop…",
      send: "Send",
      greeting:
        "Hi! I'm Flora, your Euzerone agricultural advisor. Tell me which crop you grow or what issue you see in the field and I'll recommend the right product.",
      sample_q1: "How do I correct iron chlorosis in citrus?",
      sample_q2: "I have fruit set issues in tomato",
      sample_q3: "I want to improve grape fattening",
      thinking: "Flora is thinking…",
      error: "Something went wrong. Please try again.",
      open: "Talk to Flora",
    },
    quote: {
      title: "Request a quote",
      subtitle: "Tell us about your crop and we'll get back within 24h.",
      name: "Full name",
      email: "Email",
      phone: "Phone (optional)",
      company: "Company / Farm",
      country: "Country",
      product: "Product of interest",
      quantity: "Estimated quantity",
      message: "Message or crop details",
      submit: "Send request",
      success: "Request sent! We'll be in touch soon.",
      error: "Something went wrong. Please try again.",
      any_product: "— Select a product —",
      submitting: "Sending…",
    },
    footer: {
      tagline: "Premium plant nutrition for professional farming.",
      products: "Products",
      company: "Company",
      contact: "Contact",
      legal: "Legal",
      rights: "All rights reserved.",
    },
  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "es";
    return localStorage.getItem("eu_lang") || "es";
  });

  useEffect(() => {
    localStorage.setItem("eu_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = translations[lang];
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
